<script setup lang="ts">
import {LazyHydrationWrapper} from 'vue3-lazy-hydration';
import {useI18n} from 'vue-i18n';
import {computed, onMounted, ref} from 'vue';
import {useScroll} from '@vueuse/core';
import useCpBem from '~/composables/useCpBem';
import {useProductDetails, useUpdateWishlistInfo, useGetWarrantySupplier} from '~/composables/queries/details';
import {useAddToBasket} from '~/composables/queries/basket';
import CpButton from '~/components/cp/CpButton/CpButton.vue';
import CpSectionPdpReviews from '~/components/cp/CpSection/CpSectionPdpReviews/CpSectionPdpReviews.vue';
import {useParseSeoUrlStore, usePdp, useWishlistStore} from '~/stores';
import {useCpUser} from '~/composables/useCpUser';
import CpSectionPdpQnA from '~/components/cp/CpSection/CpSectionPdpQnA/CpSectionPdpQnA.vue';
import type {IWishlist, ProductDetailResponse} from '~/types';
import {useCheckoutStore} from '~/stores';
import CpSectionPdpRowRowProduct from '~/components/cp/CpSectionPdpRowRowProduct/CpSectionPdpRowRowProduct.vue';
import CpSectionPdpWarrantyModule from '~/components/cp/CpSectionPdpWarrantyModule/CpSectionPdpWarrantyModule.vue';
import CpSectionPdpAboutManufacturer from '~/components/cp/CpSection/CpSectionPdpAboutManufacturer/CpSectionPdpAboutManufacturer.vue';
import {
  createWishlistAction,
  manageProductInWishlistAction,
  getDefaultList,
  getIsProductInList,
} from '~/composables/useWishlistActions';
import type {IWishlistPayload} from '~/utils/services/api/account/wishlists/wishlists';
import {useCpNavigateToParams} from '~/composables/useCpNavigateToParams';
import type {BreadCrumbItem} from '~/composables/useCatalogBreadCrumb';
import {useAddToProductHistory} from '~/composables/queries/product-history';
import type {ILastSeenWidget} from '~/utils/services/api';
import {generatePdpJsonLd, generateBreadcrumbsJsonLd} from '~/utils/jsonld';
import {useGTM} from '~/composables/useGTM';
import {useGetPaypalInstallmentsMSI} from '~/composables/queries/paymentMethods';
import {msiTypesEquivalences} from '~/composables/useMSIconfig';
import type {msiKinds} from '~/composables/useMSIconfig';
import {WARRANTY_TYPES, formatAsCurrency, isAppleDeviceAndRunningSarafi} from '~/utils';
import {useGetBrandsInfoByAI} from '~/composables/queries/brands';

definePageMeta({
  layout: 'default',
  name: 'details',
  alias: ['/details/:id', '/article/:id', '/product/:id'],
});

const headers = useRequestHeaders(['user-agent']);

const scrollElement = ref<HTMLElement | null>(null);
const {directions, arrivedState} = useScroll(scrollElement, {offset: {top: 100}});
const {top: isScrollingToTop} = toRefs(directions);
const {top} = toRefs(arrivedState);

const showGoToTopBtn = ref(false);
const {b, e} = useCpBem('cp-details');

const {t} = useI18n();
const $gtm = useGTM();
const parseSeoUrl = useParseSeoUrlStore();
const wishlistStore = useWishlistStore();
const authStore = useCpUser();
const store = usePdp();
const checkoutStore = useCheckoutStore();

const loading = ref(false);
const snackbarActionText = ref('');
const snackBarBody = ref('');
const useSnackBarAction = ref(true);

const showNeedsLoginModal = ref(false);
const showCreateWishlistModal = ref(false);
const showWishlistManagementModal = ref(false);
const managementModalKind = ref<
  'MoveToAList' | 'SaveInListPredetermined' | 'SaveInList' | 'AddToList' | 'DeleteFromList'
>('SaveInList');
const showSnackbar = ref(false);
const showAddWarrantiesModal = ref<boolean>(false);
const showAIGInfoModal = ref<boolean>(false);
const addToCartActionType = ref<'singleProduct' | 'multipleProducts' | 'warranty'>('singleProduct');
const selectedProductCartAmount = ref(1);
const productWishlists = ref<Array<IWishlist> | null>([]);
const allUserWishlists = ref<Array<IWishlist> | null>([]);
const displayGeneralErrorAlertBasket = ref<boolean>(false);
const messageErrorAlertBasket = ref<string>('');
const showMSIModal = ref(false);

const showCreateAlertModal = ref(false);

const productDetails = computed(() => store.productDetails ?? null);

const route = useRoute();

const historyWidget = ref<ILastSeenWidget>({
  title: 'Historial',
  articles: [],
  type: 'lastSeen',
});
/* TODO
 * Check that the useArticle call is already cashed by Nuxt
 * Load a basic pdp with that article data
 * */
const updateWishlistsInfo = async () => {
  // Wishlist where this product is
  try {
    const [{data: productList}, {data: wishlists}] = await useUpdateWishlistInfo(store.urlId, false);
    productWishlists.value = productList.value;
    allUserWishlists.value = wishlists.value;
  } catch (error) {
    // We should handle this? The method is already called in a watcher.
    // It's non-critical if we don't show if the product is in a wishlist.
  }
};

const handleSnackbarVisibility = (bodyText: string, actionText: string) => {
  snackBarBody.value = bodyText;
  snackbarActionText.value = actionText;
  showSnackbar.value = true;
};

const showLoginModal = () => {
  showNeedsLoginModal.value = false;
  useEvent('show-login-modal', {show: true});
};

const updateWishlist = async (wishlist: IWishlist, remove = false, snackbar = true) => {
  loading.value = true;
  await manageProductInWishlistAction(wishlist, store.urlId, remove);
  if (snackbar) {
    handleSnackbarVisibility(
      `${
        remove ?
          t('productDeleted') :
          wishlist.isDefault ?
          t('productSavedInYourDefaultList') :
          t('productSavedInYourList')
      } ${wishlist.title}`,
      remove ?
        t('undo') :
        wishlist.isDefault && allUserWishlists.value && allUserWishlists.value?.length > 1 ?
        t('moveProductToAnotherList') :
        t('goToFavorites'),
    );
  }
  await updateWishlistsInfo();
  loading.value = false;
};

const moveToList = async (wishlist: IWishlist) => {
  // remove it from last list
  const actionData = wishlistStore.getLastWishlistAction;
  if (actionData?.wishlist) {
    await updateWishlist(actionData?.wishlist, true, false);
  }
  // add to current list
  await updateWishlist(wishlist, false, false);
  handleSnackbarVisibility(`${t('productMovedToList')} ${wishlist.title}`, t('goToFavorites'));
};

const handleProduct = async (mainBtn = true, remove = false) => {
  if (!authStore.userId) {
    showNeedsLoginModal.value = true;
  } else {
    loading.value = true;
    if (allUserWishlists.value?.length) {
      if (mainBtn) {
        if (remove) {
          // Just predeterminated list exists
          if (allUserWishlists.value.length === 1) {
            await updateWishlist(allUserWishlists.value[0], true);
          } else {
            showWishlistManagementModal.value = true;
            managementModalKind.value = 'DeleteFromList';
          }
        } else {
          const defaultWishlist = getDefaultList(allUserWishlists.value);
          if (defaultWishlist) {
            await manageProductInWishlistAction(
              defaultWishlist,
              productDetails.value ? productDetails.value.article.id : '',
              remove,
            );
          }
          await updateWishlistsInfo();
          handleSnackbarVisibility(
            `${t('productSavedInYourDefaultList')} ${defaultWishlist?.title}`,
            allUserWishlists.value.length > 1 ? t('moveProductToAnotherList') : t('goToFavorites'),
          );
        }
      } else {
        // If product is contained in all lists, don´t open the modal
        if (allUserWishlists.value.length === productWishlists.value?.length && productWishlists.value?.length > 1) {
          handleSnackbarVisibility(t('productInAllLists'), t('saveInNewList'));
          loading.value = false;
          return;
        }
        if (allUserWishlists.value.length === 1 && getIsProductInList(allUserWishlists.value[0], store.urlId)) {
          handleSnackbarVisibility(
            `${t('productAlreadyAdded')} ${allUserWishlists.value[0].title} ${t('previously')}`,
            t('saveInNewList'),
          );
          loading.value = false;
          return;
        }
        managementModalKind.value = productWishlists.value?.length ? 'AddToList' : 'SaveInList';
        showWishlistManagementModal.value = true;
      }
      loading.value = false;
    } else {
      showCreateWishlistModal.value = true;
      loading.value = false;
    }
  }
};

const createList = async (payload: IWishlistPayload) => {
  loading.value = true;
  const list = await createWishlistAction(payload, productDetails.value?.article.id);
  await updateWishlistsInfo();
  handleSnackbarVisibility(`${t('productSavedInYourDefaultList')} ${list}`, t('goToFavorites'));
  showWishlistManagementModal.value = false;
  loading.value = false;
};

const handleSnackBarEvent = async () => {
  switch (snackbarActionText.value) {
    case t('goToFavorites'): {
      navigateTo({name: 'account-favorites'});
      break;
    }
    case t('undo'): {
      const actionData = wishlistStore.getLastWishlistAction;
      if (actionData?.wishlist && actionData.productId) {
        await manageProductInWishlistAction(actionData?.wishlist, actionData?.productId);
      }
      await updateWishlistsInfo();
      break;
    }
    case t('moveProductToAnotherList'): {
      managementModalKind.value = 'MoveToAList';
      showWishlistManagementModal.value = true;
      break;
    }
    case t('saveInNewList'):
      showCreateWishlistModal.value = true;
      break;
    default:
      break;
  }
};
const handleSendAlertMail = async (payload: { email: string; articleId: string }) => {
  const {data} = await useNoStockSubscription(payload);
  const text = typeof data.value?.msg === 'string' ? t('alertAlreadyCreated') : t('alertCreated');
  handleSnackbarVisibility(`${text} ${payload.email} ${t('productAvailable')}`, '');
};
function sendAlertValidate() {
  if (authStore.userEmail) {
    const handleSendAlertMailSimple = async () => {
      const payload = {
        email: authStore.userEmail || '',
        articleId: store.getProductId || '',
      };
      const {data} = await useNoStockSubscription(payload);
      const text = typeof data.value?.msg === 'string' ? t('alertAlreadyCreated') : t('alertCreated');
      handleSnackbarVisibility(`${text} ${payload.email} ${t('productAvailable')}`, '');
    };
    handleSendAlertMailSimple();
  } else {
    showCreateAlertModal.value = true;
  }
}

const breadcrumbItems = computed(() => {
  const {getProductBreadcrumbs} = usePdp();
  const breadcrumbsList: BreadCrumbItem[] = [];
  breadcrumbsList.push({label: 'Inicio', value: useCpNavigateToParams('home')});
  getProductBreadcrumbs?.forEach((item) => {
    breadcrumbsList.push({label: item.title, value: item.url});
  });
  return breadcrumbsList;
});

watch(
  () => authStore.user.value,
  async () => {
    if (authStore.user.value) {
      await updateWishlistsInfo();
    }
  },
);
const addToBasketLoading = ref(false);
const successAddToBasketModal = ref(false);
const displayModalRestrict = ref(false);
const displayAlertBasket = ref(false);
const merakiAlert = ref(false);

const hasAIGWarranties = computed(
  () =>
    productDetails.value?.warranties &&
    productDetails.value?.warranties.length &&
    productDetails.value?.warranties[0].type === WARRANTY_TYPES.AIG_WARRANTY,
);

const handleBasketError = (error: Error | null = null) => {
  if (error && error.data && error.data.code === 1000) {
    // Error code 1000 applies to multiple kind of products, please add specific categories logic here
    if (productDetails.value?.article.brand.title === 'AIG') {
      displayAlertBasket.value = true;
    } else {
      merakiAlert.value = true;
    }
  } else if (error && error.data && error.data.msg) {
    displayGeneralErrorAlertBasket.value = true;
    messageErrorAlertBasket.value = error?.data.msg;
  }
};

const handleAddToBasket = async () => {
  addToBasketLoading.value = true;
  if (displayRestrictMessage.value) {
    displayModalRestrict.value = true;
    addToBasketLoading.value = false;
  } else {
    const {data, error} = await useAddToBasket(store.urlId, selectedProductCartAmount.value);
    if (error.value) {
      handleBasketError(error.value);
      addToBasketLoading.value = false;
    } else if (data) {
      // NOTE: please keep addToCartActionType updated is important to AIG flow
      if (hasAIGWarranties.value) {
        showAddWarrantiesModal.value = true;
      } else {
        addToCartActionType.value = 'singleProduct';
        successAddToBasketModal.value = true;
      }
      // GTM
      const gtm = useGTM();
      if (store.getProduct) {
        gtm?.trackEvent({
          event: 'add_to_cart',
          ecommerce: {
            items: [
              {
                item_id: store.getSku,
                item_name: store.getProductId,
                variant: store.getProduct.variantName,
                price: store.getProductPrice,
                quantity: selectedProductCartAmount.value,
              },
            ],
          },
        });
      }
    }
    addToBasketLoading.value = false;
  }
};
const goToBasket = () => {
  const result = useCpNavigateToParams('basket');
  navigateTo(result);
};

const isOtStock = computed(() => {
  return store.getProduct?.stockStatus === -1 || store.getProduct?.eol;
});

const isOos = computed(() => {
  return store.getProduct?.stockStatus === -1 && !store.getProduct?.eol;
});

const isEol = computed(() => {
  return store.getProduct?.eol;
});

const displayImportedBadge = computed(() => {
  return !!productDetails.value?.article?.warranty?.active &&
    !productDetails.value?.article?.isCountryVersionMex &&
    productDetails.value?.article?.warranty?.type === 'provider';
});

const formattedWarrantyKind = computed(() => {
  if (productDetails.value?.article?.warranty?.type) {
    if (productDetails.value?.article?.warranty?.type === 'cyberpuerta') return 'Cp';
    if (productDetails.value?.article?.warranty?.type === 'manufacturer') return 'Brand';
    if (productDetails.value?.article?.warranty?.type === 'provider') {
        // settings could be null
        if (productDetails.value?.article?.warranty?.settings?.needs_folio) {
            return 'FolioRequired';
        } else {
            return 'Provider';
        }
    }
  } else {
    if (productDetails.value?.article?.isEsd) {
      // dont display warranty badge for ESD products
      return '';
    } else {
      // display provider by default
      return 'Provider';
    }
  }
  return 'Provider';
});

const addLastSeenExecute = async () => {
  try {
    return await useAddLastSeen(store.getProductId, false);
  } catch {}
};

/**
 * Only with HTTPS
 * Only to web-mobile
 * Ref: https://developer.mozilla.org/en-US/docs/Web/API/Navigator/share
 */
const share = () => {
  const shareObject = {
    url: store.getProduct?.link,
    title: store.getProduct?.title,
  };

  if (navigator.canShare(shareObject)) {
    navigator.share(shareObject);
  }
};

const init = async () => {
  checkoutStore.paypalInstallmentsMSI = null;
  store.setIsLoading(true);
  const link = (route.params.link as string[]).join('/');
  const urls = parseSeoUrl.getUrls;
  const url = route.fullPath;

  if (url in urls && urls[url]) {
    const parse = urls[url] as ProductDetailResponse;
    const urlId = parse.anid;
    store.setUrlId(urlId);
  } else {
    /**
    *  We add a slash to match with the key we save in plugins/route.ts and get data from cache
    */
    const parse = await useParseSeoUrl('/' + link);
    const urlId = parse.data.value.anid;
    if (parse.data.value.cl === 'details_af') {
      return navigateTo({
        name: 'af-details',
        params: useRoute().params,
      });
    }
    store.setUrlId(urlId);
  }

  const {data} = await useProductDetails(store.urlId, {
    transform: (input) => {
      const {article, specs, seoTitle, metaDesc, mainSpecs, breadCrumbs, questionsLink, description, iframeUrlDescription, longDescriptionUrl} = input;
      return {
        article,
        description,
        specs,
        seoTitle,
        metaDesc,
        mainSpecs,
        breadCrumbs,
        questionsLink,
        iframeUrlDescription,
        longDescriptionUrl,
      };
    },
  });

  const metaDescription = computed(() => productDetails.value?.metaDesc?.substring(0, 5000) ?? '');
  if (data.value) {
    // get warranty supplier data for pdp
    if (data.value.article) {
      try {
        data.value.article.warranty = await useGetWarrantySupplier(store.urlId);
      } catch {
        data.value.article.warranty = {};
      }
      const {data: brandInfo} = await useGetBrandsInfoByAI(data.value.article.brand.id, data.value.article.category.id);
      store.setBrandInfo(brandInfo.value);
    }
    // reset alternative product
    store.setProductAlternative(null);

    store.setPdp(data.value);
    const parse = await useParseSeoUrl('/' + link);

    let canonical = data.value?.article?.link;
    if (parse.data.value?.seo?.canonical) {
      canonical = DOMAIN + '/' + parse.data.value.seo.canonical;
    }

    // Set SEO metadata and structures data
    useHead({
      script: [
        {
          type: 'application/ld+json',
          children: () => (data.value ? generatePdpJsonLd(data.value) : null),
          key: `pdp-product-schema`,
        },
        {
          type: 'application/ld+json',
          children: () => (data.value ? generateBreadcrumbsJsonLd(data.value) : null),
          key: `pdp-breadcrumb-schema`,
        },
      ],
      link: [{
        rel: 'canonical',
        href: canonical,
        hreflang: data.value ? data.value.seo?.hreflang : '',
      }],
    });
    useSeoMeta({
      title: data.value.seoTitle ? data.value.seoTitle : data.value.article.title ?? '',
      description: metaDescription,
      ogDescription: metaDescription,
      ogUrl: data.value.article.link,
      ogImage: data.value.article.picture,
      ogImageWidth: '153',
      ogImageHeight: '153',
      ogType: 'website',
      ogTitle: productDetails.value?.seoTitle ? productDetails.value?.seoTitle : productDetails.value?.article.title ?? '',
    });
    // SEO
    useServerSeoMeta({
      title: productDetails.value?.seoTitle ? productDetails.value?.seoTitle : productDetails.value?.article.title ?? '',
      description: metaDescription,
      ogDescription: metaDescription,
      ogUrl: productDetails.value?.article.link ?? '',
      ogImage: productDetails.value?.article.picture ?? '',
      ogImageWidth: '153',
      ogImageHeight: '153',
      ogType: 'website',
      ogTitle: productDetails.value?.seoTitle ? productDetails.value?.seoTitle : productDetails.value?.article.title ?? '',
    });

    // get warranty national version alternative
    if (data.value?.article?.warranty?.nationalVersion) {
      const {data: nationalVersionProduct} = await useProductDetails(data.value.article.warranty.nationalVersion, {
      transform: (input) => {
        const {article} = input;
          return {
            article,
          };
        },
      });

      store.setProductAlternative( nationalVersionProduct.value || {} );
    }
  }
  store.setIsLoading(false);
};

const initClient = async () => {
  const data = store.productDetails;
  addLastSeenExecute();
  if (authStore.isAuthenticated.value && data) {
    await useAddToProductHistory(data.article.id, false);
  }

  if (authStore.userId && data) {
    await updateWishlistsInfo();
  }
  const {data: installmentsData} = await useGetPaypalInstallmentsMSI(data?.article?.price || 0, false);
  if (installmentsData.value) {
    checkoutStore.paypalInstallmentsMSI = installmentsData.value;
  }
};

const isWarranty = computed(() => {
  return Object.values(WARRANTY_TYPES).includes(store.getProduct?.type ?? '');
});
const isAIG = computed(() => {
  return isWarranty.value && store.getProduct?.type === '62c8d733a974d2.28868521';
});
const displayRestrictMessage = computed(() => {
  return isWarranty.value && !isAIG.value;
});

const timer = ref<ReturnType<typeof setTimeout>>();

onMounted(async () => {
  if (!store.renderedOnServer) {
    // We need to execute first init to update pdp store data so we shouldn't use promise.all in this case
    await init();
  }
  await initClient();
  const data = store.productDetails;
  if (data) {
    // Using nextTick to wait for Vue to update the DOM before tracking the event IT-21001
    nextTick(() => {
      // Using setTimeout with a small delay to allow other operations to complete before tracking the event IT-21001
      timer.value = setTimeout(() => {
        $gtm?.trackEvent({
          event: 'view_item',
          item_id: data.article?.sku,
          item_name: data.article.title,
          price: data.article.price,
          item_variant: data.article.variantName,
          brand: data.article.brand ? data.article?.brand?.title : {},
          category: data.article.brand ? data.article?.category?.title : {},
          discount: data.article.tPrice - parseInt(data.article.price.toString(), 10),
          ean: data.article.ean,
          desc: data.description,
          productId: data.article.id,
        });
      }, 500);
    });
  }
  store.setRenderedOnServer(false);
});

onBeforeUnmount(() => {
  if (timer.value) {
    clearTimeout(timer.value);
  }
});

if (process.server) {
  store.setRenderedOnServer(true);
  await init();
}

const handleScrollBehavior = () => {
  if (top.value) {
    showGoToTopBtn.value = false;
    return;
  }
  if (isScrollingToTop.value) {
    showGoToTopBtn.value = true;
  } else {
    showGoToTopBtn.value = false;
  }
};

const toTop = () => {
  scrollElement.value?.scrollTo({top: 0, behavior: 'smooth'});
  showGoToTopBtn.value = false;
};

const addAIGWarrantyToBasket = async (data: string | Array<string>) => {
  loading.value = true;
  // If payload is array it'll ignore amount variable
  const formattedPayload: string | Array<{ amount: number; id: string }> = Array.isArray(data) ?
    data.map((articleId) => {
        return {id: articleId, amount: selectedProductCartAmount.value};
      }) :
    data;
  const {error} = await useAddToBasket(formattedPayload, selectedProductCartAmount.value);
  if (error.value) {
    handleBasketError(error.value);
  } else {
    addToCartActionType.value = Array.isArray(data) ? 'multipleProducts' : 'warranty';
    showAddWarrantiesModal.value = false;
    successAddToBasketModal.value = true;
    const selectedWarranty = productDetails.value?.warranties.find(
      (warranty) => warranty.id === (Array.isArray(data) ? data[1] : data),
    );
    if (selectedWarranty) {
      // GTM
      const gtm = useGTM();
      gtm?.trackEvent({
        event: 'add_to_cart',
        ecommerce: {
          items: [
            {
              item_id: selectedWarranty.sku,
              item_name: selectedWarranty.id,
              variant: '',
              price: selectedWarranty.priceValue,
              quantity: selectedProductCartAmount.value,
            },
          ],
        },
      });
    }
  }
  loading.value = false;
};
const warrantiesLink = 'https://www.cyberpuerta.mx/aig-garanplus/';

const showInstallmentsData = () => {
  if (checkoutStore.paypalInstallmentsMSI) {
    if (checkoutStore.paypalInstallmentsMSI.data.msi.type === 1) {
      showMSIModal.value = true;
    } else {
      navigateTo({name: 'monthly-payment', query: {origin: 'pdp'}});
    }
  } else {
    console.log('No installments to show');
  }
};

  useHead({
    link: [
      {
        rel: 'preload',
        as: 'image',
        href: productDetails.value?.article.pictures[0],
        fetchpriority: 'high',
      },
    ],
    meta: [{
      name: 'apple-itunes-app',
      content: 'app-id=1636030641',
    }],
  });

  const defineMSIKind = (): msiKinds => {
    return msiTypesEquivalences[checkoutStore?.paypalInstallmentsMSI?.data?.msi?.type as keyof typeof msiTypesEquivalences] as msiKinds || 'msiOff';
  };

</script>

<template>
    <div v-if="productDetails && !store.isLoading"
        ref="scrollElement"
        :class="b"
        data-testid="details"
        @scroll="handleScrollBehavior"
    >
        <CpNotificationBannerPromoteApp v-if="!isAppleDeviceAndRunningSarafi(headers['user-agent'])" />
        <LazyHydrationWrapper :when-idle="true">
            <CpBreadcrumb :items="breadcrumbItems" :side-padding="false" />
        </LazyHydrationWrapper>
        <LazyCpAlertHeader
            v-if="productDetails.article.hideAddToCartButton || productDetails.article.notBuyable"
            :subheading="t('cantBuyAloneProduct')"
            :body="t('cantBuyAloneProductBody')"
        />
        <LazyCpAlertHeader v-else-if="isOos" :subheading="t('outOfStockTitle')" :body="t('outOfStockSubtitle')" />
        <LazyCpAlertHeader v-else-if="isEol" :subheading="t('eolTitle')" :body="t('eolSubTitle')" />
        <LazyCpAlertHeader v-else-if="displayRestrictMessage" :subheading="t('warrantyMessage')" :body="t('goToCp')" />
        <CpSectionPdpView
            :product="productDetails.article"
            :product-lives-in-list="!!productWishlists?.length"
            critical
            @main-wishlist-btn-pressed="handleProduct(true, $event)"
            @add-to-another-wishlist="handleProduct(false)"
            @share="share"
        />
        <div v-if="!productDetails.article.hideAddToCartButton">
            <LazyHydrationWrapper v-if="productDetails.article.isDailyOffer" :when-idle="true">
                <CpSectionPdpFeaturedProduct
                    v-if="productDetails.article.isDailyOffer"
                    :product="productDetails.article"
                />
            </LazyHydrationWrapper>
            <LazyHydrationWrapper :when-idle="true">
                <CpSectionPdpPriceShipping :product="productDetails.article" />
            </LazyHydrationWrapper>
            <LazyHydrationWrapper v-if="!isOtStock" :when-idle="true">
                <RowEta v-if="!isOtStock" :product="productDetails.article" />
            </LazyHydrationWrapper>
            <LazyHydrationWrapper v-if="!isOtStock" :when-idle="true">
                <LazyCpSectionPdpStock
                    v-if="!isOtStock"
                    :selected-pieces="selectedProductCartAmount"
                    :esd="store.getProductIsEsd"
                    :stock="store.getProductStock.stock"
                    :low-stock="store.getProductStock.lowStock"
                    @add-product="selectedProductCartAmount = $event"
                />
            </LazyHydrationWrapper>
            <LazyHydrationWrapper v-if="displayImportedBadge" :when-idle="true">
                <LazyCpSectionPdpRowRowProduct
                    v-if="displayImportedBadge"
                    :display-product="!!store.productAlternative?.article &&
                        store.productAlternative?.article?.stock > 0"
                    :product="store.productAlternative?.article"
                    :manage-by="(store.productAlternative?.article?.warranty?.title || '')?.replace(/<[^>]*>/g, ' ')"
                />
            </LazyHydrationWrapper>
            <LazyCpSectionInstallmentsOverview
                v-if="checkoutStore.paypalInstallmentsMSI"
                origin="pdp"
                :kind="defineMSIKind()"
                hide-top-border
                :msi-data="checkoutStore.paypalInstallmentsMSI?.data.msi"
                :installments="checkoutStore.paypalInstallmentsMSI?.data.installments"
                @show-info="showInstallmentsData()"
            />
            <LazyCpSectionPdpRowCoupon v-if="productDetails.article?.vouchers?.length" />
            <LazyCpSectionPdpRowBrandStore
                v-if="productDetails?.article?.brand"
                :brand-name="productDetails.article.brand.title"
                :image="productDetails.article.brand.image"
                :link="productDetails.article.brand.link"
            />
            <LazyHydrationWrapper v-if="productDetails.mainSpecs" :when-idle="true">
                <LazyCpSectionPdpCharacteristics
                    v-if="productDetails.mainSpecs"
                    :main-specs="productDetails.mainSpecs"
                    @show-full-specs="navigateTo({ name: 'details-specs', params: { id: store.getProductId } })"
                />
            </LazyHydrationWrapper>
            <LazyHydrationWrapper v-if="!!formattedWarrantyKind" :when-idle="true">
                <CpSectionPdpWarrantyModule :class="e`warranty-badge`"
                    v-bind="{
                        brandName: productDetails.article?.brand?.title || '',
                        kind: formattedWarrantyKind,
                        warrantyTime: productDetails?.article?.warranty?.years || 1,
                        title: productDetails?.article?.warranty?.title,
                    }"
                />
            </LazyHydrationWrapper>
            <LazyHydrationWrapper v-if="hasAIGWarranties" :when-visible="{ rootMargin: '50px'}">
                <LazyCpCrossSellingWarrantyBundle
                    v-if="hasAIGWarranties"
                    :product="productDetails.article"
                    :warranties="productDetails.warranties"
                    @show-info="showAIGInfoModal = true"
                    @add-to-basket="addAIGWarrantyToBasket($event)"
                />
            </LazyHydrationWrapper>
        </div>
        <ClientOnly>
            <LazyHydrationWrapper v-if="!productDetails.article.hideAddToCartButton" :when-visible="{rootMargin:'0%'}">
                <CpCrossSellingRaiPdp v-if="!productDetails.article.hideAddToCartButton" kind="similar" />
            </LazyHydrationWrapper>
            <LazyHydrationWrapper>
                <CpSectionPdpLongDescription
                    v-if="productDetails?.description"
                    :kind="productDetails.iframeUrlDescription ? 'SyndicatedContent' : 'Text'"
                    :text="productDetails?.description"
                    :url="productDetails.iframeUrlDescription"
                />
            </LazyHydrationWrapper>
            <LazyHydrationWrapper :when-visible="{rootMargin:'1px'}">
                <CpSectionPdpReviews id="reviews-section" :reviews-to-show="4" :is-logged="authStore.isAuthenticated.value" />
            </LazyHydrationWrapper>
            <LazyHydrationWrapper :when-visible="{rootMargin:'1px'}">
                <CpSectionPdpQnA id="questions-section" />
            </LazyHydrationWrapper>
            <LazyHydrationWrapper :when-visible="{rootMargin:'1px'}">
                <CpCrossSellingRaiPdp kind="boughtTogether" />
            </LazyHydrationWrapper>
            <LazyHydrationWrapper v-if="!productDetails.article.hideAddToCartButton" :when-visible="{rootMargin:'1px'}">
                <LazyCpSectionPdpSpecifications
                    v-if="!productDetails.article.hideAddToCartButton"
                    :specs="productDetails.specs"
                    pdp
                    @button-click="navigateTo({ name: 'details-specs', params: { id: store.getProductId } })"
                />
            </LazyHydrationWrapper>
        </ClientOnly>
        <CpSectionPdpAboutManufacturer
            v-if="store.brandInfo"
            :brand-name="productDetails?.article?.brand?.title"
            :image="productDetails?.article?.brand?.image"
            :body="store.brandInfo?.data?.attributes?.text"
        />
        <ClientOnly>
            <LazyHydrationWrapper :when-visible="{rootMargin:'1px'}">
                <CpCrossSellingRaiPdp kind="recommendations" />
            </LazyHydrationWrapper>
            <v-lazy transition="fade-transition">
                <LazyHomeViewHistory :widget="historyWidget" />
            </v-lazy>
        </ClientOnly>
        <div v-if="productDetails && !productDetails.article.hideAddToCartButton" :class="e('basket-btn')" data-testid="basket-button">
            <CpButton
                v-if="isOos"
                label="Crear alerta de disponibilidad"
                icon="add_alert"
                kind="iconlabel"
                type="button"
                color="primary"
                full-width
                data-testid="add_alert"
                @click="sendAlertValidate"
            />
            <CpButton
                v-else-if="isEol"
                label="Regresar a la categoría"
                icon="arrow_back"
                kind="iconlabel"
                type="button"
                color="primary"
                full-width
                data-testid="add_alert"
                @click="navigateTo(useCpNavigateToParams('catalog', store.getProduct?.category.link))"
            />
            <CpButton
                v-else-if="!productDetails.article.notBuyable"
                label="Agregar al carrito"
                icon="add_shopping_cart"
                kind="iconlabel"
                type="button"
                color="secondary"
                :loading="addToBasketLoading"
                full-width
                data-testid="add-to-cart-pdp"
                @click="handleAddToBasket"
            />
        </div>
        <div v-if="showGoToTopBtn" :class="e('button-container')">
            <LazyCpButtonRound v-if="showGoToTopBtn" icon="goToTop" @click="toTop" />
        </div>
        <LazyCpModalDataEntryWishlist
            v-if="showCreateWishlistModal"
            v-model:show="showCreateWishlistModal"
            kind="AddProduct"
            @wishlist-create="createList($event)"
        />
        <LazyCpModalDialog
            v-if="showNeedsLoginModal"
            v-model:show="showNeedsLoginModal"
            :body="t('addProductToListWarning')"
            :title="t('notice')"
            type-first-button="primary"
            :label-first-button="t('login')"
            @first-button="showLoginModal"
        />
        <LazyCpModalDialog
            v-if="successAddToBasketModal"
            v-model:show="successAddToBasketModal"
            :title="t('successAction')"
            :subheading="
                addToCartActionType === 'singleProduct'
                    ? t('productAddedToBasketTitle')
                    : addToCartActionType === 'multipleProducts'
                        ? t('productsAddedToBasketTitle')
                        : t('warrantyAddedToBasketTitle')
            "
            :body="
                addToCartActionType === 'singleProduct'
                    ? t('productAddedToBasketBody')
                    : addToCartActionType === 'multipleProducts'
                        ? t('productsAddedToBasketBody')
                        : t('warrantyAddedToBasketBody')
            "
            :label-first-button="t('goToBasketText')"
            kind="success"
            type-first-button="primary"
            @first-button="goToBasket"
        />
        <LazyCpModalDialog
            v-if="displayModalRestrict"
            v-model:show="displayModalRestrict"
            :title="t('warning')"
            :subheading="t('restrictTitle')"
            :body="t('restrictMessage')"
            :label-first-button="t('back')"
            kind="critical"
            @first-button="displayModalRestrict = false"
        />
        <LazyCpModalDialog
            v-if="merakiAlert"
            v-model:show="merakiAlert"
            :title="t('limitedPurchase')"
            :subheading="t('limitedArticle')"
            :body="t('limitedArticleMessage')"
            :label-first-button="t('back')"
            kind="critical"
            @first-button="merakiAlert = false"
        />
        <LazyCpModalDialog
            v-if="displayAlertBasket"
            v-model:show="displayAlertBasket"
            :title="t('limitedPurchase')"
            :subheading="t('limitedPurchaseTitle')"
            :body="t('limitedPurchaseMessage')"
            :label-first-button="t('back')"
            kind="info"
            type-first-button="primary"
            @first-button="displayAlertBasket = false"
        />
        <LazyCpModalDialog
            v-if="displayGeneralErrorAlertBasket"
            v-model:show="displayGeneralErrorAlertBasket"
            :title="t('warning')"
            :subheading="messageErrorAlertBasket.includes('session') ? t('restrictTitle') : t('unexpectedError')"
            :body="messageErrorAlertBasket.includes('session') ? messageErrorAlertBasket : t('reportUnexpectedError')"
            :label-first-button="messageErrorAlertBasket.includes('session') ? t('back') : t('retry')"
            kind="critical"
            use-bold-text
            type-first-button="primary"
            @first-button="displayGeneralErrorAlertBasket = false"
        />
        <LazyCpModalSelectionWishlist
            v-if="store.urlId && allUserWishlists && productWishlists && showWishlistManagementModal"
            v-model:show="showWishlistManagementModal"
            :wishlists="allUserWishlists"
            :product-wishlists="productWishlists"
            :product-id="store.urlId.toString()"
            :kind="managementModalKind"
            @create-new-list="showCreateWishlistModal = true"
            @add-to-list="updateWishlist($event)"
            @delete-list="updateWishlist($event, true)"
            @move-to-list="moveToList($event)"
        />
        <LazyCpSnackbar
            v-if="showSnackbar"
            v-model:show="showSnackbar"
            :time="5"
            :action="useSnackBarAction"
            :action-text="snackbarActionText"
            :body="snackBarBody"
            @update:show="showSnackbar = false"
            @snackbar:event="handleSnackBarEvent"
        />
        <LazyCpModalDataEntryCreateAlert
            v-if="showCreateAlertModal"
            v-model:show="showCreateAlertModal"
            :article-id="store.getProductId"
            @sent-email="handleSendAlertMail"
        />
        <LazyCpModalCrossSelling
            v-if="hasAIGWarranties"
            v-model:show="showAddWarrantiesModal"
            :products="productDetails.warranties"
            @add-warranty="addAIGWarrantyToBasket($event)"
        />
        <LazyCpModalDialog
            v-if="showAIGInfoModal"
            v-model:show="showAIGInfoModal"
            :title="t('notice')"
            :subheading="t('aigWarranty')"
            :body="t('protectYourProducts')"
            :label-first-button="t('back')"
            use-slot
            reduce-gap
            @first-button="showAIGInfoModal = false"
        >
            <template #body>
                <CpText variant="subheading">
                    {{ t('coverage') }}
                </CpText>
                <ul :class="e('list')">
                    <li :class="e('list-item')">
                        <CpText variant="body-regular">
                            {{ t('manufacturerWarranty') }}
                        </CpText>
                    </li>
                    <li :class="e('list-item')">
                        <CpText variant="body-regular">
                            {{ t('voltageCoverage') }}
                        </CpText>
                    </li>
                    <li :class="e('list-item')">
                        <CpText variant="body-regular">
                            {{ t('spareParts') }}
                        </CpText>
                    </li>
                    <li :class="e('list-item')">
                        <CpText variant="body-regular">
                            {{ t('replacement') }}
                        </CpText>
                    </li>
                    <li :class="e('list-item')">
                        <CpText variant="body-regular">
                            {{ t('homeService') }}
                        </CpText>
                    </li>
                    <li :class="e('list-item')">
                        <CpText variant="body-regular">
                            {{ t('serviceCenters') }}
                        </CpText>
                    </li>
                </ul>
                <CpText :class="e('text')" variant="body-regular">
                    {{ t('knowMoreAboutAIG') }}
                    <CpNuxtLink :link="warrantiesLink">
                        <CpText variant="body-link" type="primary">
                            {{ t('aigLink') }}
                        </CpText>
                    </CpNuxtLink>
                </CpText>
            </template>
        </LazyCpModalDialog>
        <LazyCpLoader v-if="loading" kind="small" full-size />
        <LazyCpModalDialog
            v-if="showMSIModal && checkoutStore.paypalInstallmentsMSI"
            v-model:show="showMSIModal"
            :title="t('monthlyPayment')"
            :subheading="t('deferredMonths')"
            :body="t('deferredMonthsDescription', [
                formatAsCurrency(checkoutStore.paypalInstallmentsMSI?.data.msi.text?.value || 0),
                Math.min(...checkoutStore.paypalInstallmentsMSI.data.installments.map(i => i.month)) || '-',
                checkoutStore.paypalInstallmentsMSI?.data.msi.text?.month || '18'
            ])"
            :label-first-button="t('back')"
            use-bold-text
            use-slot
            @first-button="showMSIModal = false"
        >
            <template #body>
                <CpText variant="caption-regular">
                    {{ t('cardPayment') }}
                </CpText>
            </template>
        </LazyCpModalDialog>
    </div>
    <LazyCpSkeletonPDP v-else />
</template>

<style scoped lang="scss">
.cp-details {
  position: relative;
  overflow: scroll;
  height: 100%;
  &__basket-btn {
    position: sticky;
    bottom: $cp-gutter;
    padding: $cp-gutter $cp-gutter 0;
    z-index: 100;
  }
  &__button-container {
    position: sticky;
    bottom: 80px;
    z-index: 100;
    width: 100%;
    display: flex;
    justify-content: flex-end;
    padding: 0 $cp-gutter 0 0;
  }
  &__list {
    padding-left: $cp-gutter;
    list-style-type: none;
    text-align: start;
    li::before {
      content: '·';
      font-size: 20px;
      vertical-align: middle;
      line-height: var(--cp-body-regular-line-height);
      margin-left: -8px;
      margin-right: $cp-gutter-small;
    }
  }
  &__list-item {
    display: flex;
  }
  &__text {
    display: flex;
    flex-direction: row;
    gap: $cp-gutter-xsmall;
    justify-content: flex-start;
    width: 100%;
  }
  &__warranty-badge {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
  }
}
</style>

<i18n locale="es-US">
  notice: 'Aviso'
  addProductToListWarning: 'Para que puedas agregar este producto a una lista de favoritos es necesario que inicies sesión.'
  login: 'Iniciar sesión'
  productSavedInYourDefaultList: 'Producto guardado en tu lista de favoritos predeterminada'
  productSavedInYourList: 'Producto guardado en tu lista de favoritos'
  goToFavorites: 'Ir a la lista de favoritos'
  undo: 'Deshacer'
  productDeleted: 'Producto eliminado exitosamente de tu lista de favoritos'
  moveProductToAnotherList: 'Mover producto a otra lista'
  productInAllLists: 'Este producto ya fue agregado a todas las listas.'
  saveInNewList: 'Guardar en una nueva lista'
  productAlreadyAdded: 'Este producto ya fue agregado a esta lista'
  previously: 'con anterioridad'
  productMovedToList: 'El producto se movió a tu lista de favoritos'
  successAction: 'Acción exitosa'
  productAddedToBasketTitle: '¡Producto agregado a tu carrito!'
  productsAddedToBasketTitle: '¡Productos agregados a tu carrito!'
  warrantyAddedToBasketTitle: '¡Garantía agregada a tu carrito!'
  productAddedToBasketBody: 'Se ha agregado el producto a tu carrito exitosamente.'
  productsAddedToBasketBody: 'El producto y su garantía se han agregado a tu carrito.'
  warrantyAddedToBasketBody: 'Hemos agregado una garantía para cada uno de los productos. Disfruta de los beneficios de la protección extendida para tu producto.'
  goToBasketText: 'Ir al carrito'

  outOfStockTitle: 'Lo sentimos, por el momento este producto está agotado.'
  outOfStockSubtitle: 'Puedes recibir una alerta a tu correo cuando esté nuevamente disponible.'

  alertCreated: 'Alerta creada. Recibirás un correo a'
  alertAlreadyCreated: 'Ya has creado una alerta. Recibirás un correo a'
  productAvailable: 'cuando el producto esté nuevamente disponible.'

  eolTitle: 'Lo sentimos, este producto no cuenta con existencias desde hace varias semanas.'
  eolSubTitle: 'Es probable que se encuentre fuera del mercado y no volvamos a tener existencia, así que te recomendamos revisar la categoría para encontrar otra alternativa.'

  warrantyMessage: "Lo sentimos, este producto solo se puede adquirir desde la versión de escritorio de Cyberpuerta."
  goToCp: "Visita nuestro sitio para finalizar la compra."
  warning: "Advertencia"
  restrictTitle: "No es posible agregar este producto al carrito"
  restrictMessage: "Lo sentimos, este producto solo se puede adquirir desde la versión de escritorio de Cyberpuerta."
  back: "Regresar"

  limitedPurchase: "Compra limitada"
  limitedPurchaseTitle: "Las garantías no se venden individualmente"
  limitedArticle: "Este producto no se vende individualmente"
  limitedArticleMessage: "Este producto no puede ser vendido individualmente, si deseas adquirirlo es necesario que incluyas algún producto compatible en tu carrito."
  limitedPurchaseMessage: "Para adquirir esta garantía es necesario que primero añadas a tu carrito el producto que quieres proteger. Te mostramos las garantías disponibles para dicho producto."

  aigWarranty: 'Garantía extendida AIG Garanplus'
  protectYourProducts: 'Protege tus productos por 1 o 2 años adicionales a la garantía del fabricante.'
  coverage: '¿Qué incluye la cobertura?'
  manufacturerWarranty: 'Garantía extendida al término de la garantía original del fabricante, sin deducibles, ni cargos ocultos o adicionales, la atención de este servicio es especializada para ti.'
  voltageCoverage: 'Cobertura por variación de voltaje desde el primer día (aplica solo 1 evento).'
  spareParts: 'Refacciones nuevas y reparaciones ilimitadas.'
  replacement: 'Reemplazo del producto si no es reparado en 30 días naturales.'
  homeService: 'Servicio a domicilio para productos de gran tamaño en línea blanca y pantallas a partir de 32”.'
  serviceCenters: 'Acceso a una extensa red de Centros de Servicio especializados en todo el país.'
  knowMoreAboutAIG: 'Conoce más acerca de las'
  aigLink: 'garantías AIG aquí.'
  retry: 'Reintentar'
  unexpectedError: 'Error inesperado'
  reportUnexpectedError: 'Lo sentimos, ocurrió un error al realizar la acción; favor de intentarlo nuevamente. Si el problema persiste, comunícate con nosotros a través del botón *Reportar un problema* localizado en el menú.'
  cantBuyAloneProduct: 'Lo sentimos, este producto no se puede adquirir individualmente.'

  monthlyPayment: 'Opciones de pago a mensualidades'
  deferredMonths: 'Meses diferidos*'
  deferredMonthsDescription: 'Con un mínimo de *{0}* en tu carrito, podrás diferir tu compra desde *{1}* hasta *{2} mensualidades*, sujeto a tu tarjeta de crédito y al costo de gestión.'
  cardPayment: '* Pagando con Tarjeta de Crédito (VISA, MasterCard, AMEX) o PayPal.'
</i18n>
